import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { AlertCircle, RotateCcw, Search, SearchX } from "lucide-react";
import { deleteInvestments } from "../../utilities/api";
import { cn } from "../../utilities/helpers";
import AddInvestment from "../AddInvestments";
import { Button } from "../Button";
import { Card } from "../card";
import AppShell from "../layout/AppShell";
import AssetTransactions from "./AssetTransactions";
import TransactionEditModal from "./TransactionEditModal";
import { useTransactionsByAsset } from "./useTransactionsByAsset";

const CONTROL_CLASS =
	"h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400";

const TYPE_FILTERS = [
	{ value: "ALL", label: "All" },
	{ value: "BUY", label: "Buys" },
	{ value: "SELL", label: "Sells" },
];

const DASHBOARD_KEYS = [
	"fetchInvestments",
	"fetchInvestmentSummary",
	"fetchAssets",
	"fetchAllAssetLatestPrice",
	"fetchDaily",
];

const TransactionsPage = () => {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [type, setType] = useState("ALL");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [editing, setEditing] = useState(null);
	const [confirmingId, setConfirmingId] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [collapsed, setCollapsed] = useState({});
	const [error, setError] = useState("");

	const {
		groups,
		assetCount,
		transactionCount,
		totalTransactionCount,
		isLoading,
		isError,
		refetch,
	} = useTransactionsByAsset({ search, type, from, to });

	const hasFilters = Boolean(search || type !== "ALL" || from || to);
	const allCollapsed = useMemo(
		() => groups.length > 0 && groups.every((group) => collapsed[group.asset]),
		[groups, collapsed]
	);

	const clearFilters = () => {
		setSearch("");
		setType("ALL");
		setFrom("");
		setTo("");
	};

	const toggleAll = () => {
		const next = {};
		if (!allCollapsed) groups.forEach((group) => (next[group.asset] = true));
		setCollapsed(next);
	};

	const deleteMutation = useMutation(deleteInvestments, {
		onSuccess: () => {
			DASHBOARD_KEYS.forEach((key) => queryClient.invalidateQueries(key));
			setDeletingId(null);
			setConfirmingId(null);
		},
		onError: (err) => {
			setError(
				err?.response?.data?.error || err.message || "Failed to delete transaction"
			);
			setDeletingId(null);
			setConfirmingId(null);
		},
	});

	const handleConfirmDelete = (row) => {
		setError("");
		setDeletingId(row.id);
		deleteMutation.mutate(row.id);
	};

	return (
		<AppShell
			title="Transactions"
			description="Every buy and sell, grouped by asset. Edits flow straight through to the dashboard."
			actions={
				groups.length > 0 && (
					<Button type="button" variant="outline" size="sm" onClick={toggleAll}>
						{allCollapsed ? "Expand all" : "Collapse all"}
					</Button>
				)
			}
		>
			<Card className="rounded-xl border-slate-200 bg-white p-3 shadow-sm md:p-4">
				<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<div className="relative">
							<label htmlFor="transaction-search" className="sr-only">
								Search assets
							</label>
							<Search
								className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
								aria-hidden="true"
							/>
							<input
								id="transaction-search"
								type="search"
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search assets"
								className={cn(CONTROL_CLASS, "w-full pl-9 sm:w-56")}
							/>
						</div>

						<div
							role="group"
							aria-label="Transaction type"
							className="flex w-fit gap-1 rounded-md bg-slate-100 p-1"
						>
							{TYPE_FILTERS.map((filter) => (
								<button
									key={filter.value}
									type="button"
									onClick={() => setType(filter.value)}
									aria-pressed={type === filter.value}
									className={cn(
										"rounded px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
										type === filter.value
											? "bg-white text-slate-900 shadow-sm"
											: "text-slate-500 hover:text-slate-900"
									)}
								>
									{filter.label}
								</button>
							))}
						</div>

						{/* Bounding each input by the other keeps the range from
						    being inverted into an always-empty result. */}
						<div className="flex flex-wrap items-center gap-2">
							<label htmlFor="transaction-from" className="text-sm text-slate-500">
								From
							</label>
							<input
								id="transaction-from"
								type="date"
								value={from}
								max={to || undefined}
								onChange={(event) => setFrom(event.target.value)}
								className={cn(CONTROL_CLASS, "tabular-nums")}
							/>
							<label htmlFor="transaction-to" className="text-sm text-slate-500">
								to
							</label>
							<input
								id="transaction-to"
								type="date"
								value={to}
								min={from || undefined}
								onChange={(event) => setTo(event.target.value)}
								className={cn(CONTROL_CLASS, "tabular-nums")}
							/>
						</div>
					</div>

					<div className="flex items-center gap-3 lg:justify-end">
						<p className="text-sm tabular-nums text-slate-500">
							{isLoading
								? "Loading…"
								: `${transactionCount} of ${totalTransactionCount} ${
										totalTransactionCount === 1 ? "entry" : "entries"
									} · ${groups.length} of ${assetCount} ${
										assetCount === 1 ? "asset" : "assets"
									}`}
						</p>
						{hasFilters && (
							<button
								type="button"
								onClick={clearFilters}
								className="rounded-md px-2 py-1 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
							>
								Clear filters
							</button>
						)}
					</div>
				</div>
			</Card>

			{error && (
				<div
					role="alert"
					className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
				>
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
					<span>{error}</span>
				</div>
			)}

			{isLoading &&
				[0, 1, 2].map((key) => (
					<Card
						key={key}
						className="rounded-xl border-slate-200 bg-white p-4 shadow-sm"
						aria-hidden="true"
					>
						<div className="animate-pulse space-y-3">
							<div className="h-4 w-32 rounded bg-slate-100" />
							<div className="h-3 w-48 rounded bg-slate-100" />
							<div className="h-24 rounded bg-slate-50" />
						</div>
					</Card>
				))}
			{isLoading && <p className="sr-only">Loading transactions</p>}

			{isError && !isLoading && (
				<Card className="rounded-xl border-slate-200 bg-white shadow-sm">
					<div className="flex flex-col items-center gap-3 p-10 text-center">
						<AlertCircle className="h-6 w-6 text-rose-600" aria-hidden="true" />
						<div>
							<h2 className="text-base font-semibold text-slate-900">
								Could not load transactions
							</h2>
							<p className="mt-1 text-sm text-slate-500">
								The server did not respond. Check your connection and try again.
							</p>
						</div>
						<Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
							<RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
							Try again
						</Button>
					</div>
				</Card>
			)}

			{!isLoading && !isError && groups.length === 0 && (
				<Card className="rounded-xl border-slate-200 bg-white shadow-sm">
					<div className="flex flex-col items-center gap-3 p-10 text-center">
						<SearchX className="h-6 w-6 text-slate-400" aria-hidden="true" />
						<div>
							<h2 className="text-base font-semibold text-slate-900">
								{hasFilters ? "No matching transactions" : "No transactions yet"}
							</h2>
							<p className="mt-1 max-w-md text-sm text-slate-500">
								{hasFilters
									? "Nothing matches the current filters. Widen the date range or clear them."
									: "Add your first buy and it will show up here, grouped by asset."}
							</p>
						</div>
						{hasFilters ? (
							<Button type="button" variant="outline" size="sm" onClick={clearFilters}>
								Clear filters
							</Button>
						) : (
							<AddInvestment />
						)}
					</div>
				</Card>
			)}

			{groups.map((group) => (
				<AssetTransactions
					key={group.asset}
					group={group}
					collapsed={Boolean(collapsed[group.asset])}
					onToggleCollapse={() =>
						setCollapsed((prev) => ({ ...prev, [group.asset]: !prev[group.asset] }))
					}
					onEdit={(row) => {
						setError("");
						setConfirmingId(null);
						setEditing(row);
					}}
					onRequestDelete={(row) => {
						setError("");
						setConfirmingId(row.id);
					}}
					onCancelDelete={() => setConfirmingId(null)}
					onConfirmDelete={handleConfirmDelete}
					confirmingId={confirmingId}
					deletingId={deletingId}
				/>
			))}

			<TransactionEditModal
				open={Boolean(editing)}
				transaction={editing}
				onClose={() => setEditing(null)}
			/>
		</AppShell>
	);
};

export default TransactionsPage;
